import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition, ZoneDefinition } from '../types';

export interface GlowstickAttack {
  kind: 'glowstick';
  cooldownTicks: number;
  flightTicks: number;
  throws: readonly number[];
  // The fan of the sticks thrown at once, and how close a stick in flight hits.
  spreadRadians: number;
  stickRadius: number;
  zone: ZoneDefinition;
  frontRow: { reach: number; bonus: number };
}

export interface RappelEffect {
  kind: 'rappel';
  damage: number;
  radius: number;
}

export const SUPERFAN: ClassDefinition = {
  id: 'superfan',
  name: 'Le Super fan',
  role: 'Plante devant la scène des bâtons lumineux qui pulsent sur le temps, et les fait tous éclater d’un rappel.',
  color: '#c77dff',
  maxHp: 120,
  speed: 7.4,
  radius: 14,
  pickupRadius: 110,
  attack: {
    kind: 'glowstick',
    cooldownTicks: TICKS_PER_BEAT,
    flightTicks: 7,
    throws: [150, 105, 195],
    spreadRadians: 0.5,
    stickRadius: 12,
    zone: {
      id: 'baton-lumineux',
      radius: 50,
      durationBars: 2,
      maxPerPlayer: 6,
      cadence: 'beat',
      damage: 6,
    },
    frontRow: { reach: 150, bonus: 0.5 },
  },
  skill: {
    id: 'rappel',
    name: 'Rappel',
    description: 'Tous tes bâtons lumineux éclatent d’un coup, puis se rallument.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: { kind: 'rappel', damage: 25, radius: 70 },
  },
  affinityWeaponIds: ['totem'],
};

export const SUPERFAN_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'fosse-illuminee',
    name: 'Fosse illuminée',
    description: 'Tu plantes deux bâtons lumineux de plus à la fois.',
    family: 'class',
    classId: 'superfan',
    modifiers: [{ stat: 'zoneCountAdd', add: 2 }],
    rarities: {
      rare: {
        description: 'Tu plantes quatre bâtons lumineux de plus à la fois.',
        modifiers: [{ stat: 'zoneCountAdd', add: 4 }],
      },
      legendary: {
        description: 'Tu plantes six bâtons lumineux de plus à la fois.',
        modifiers: [{ stat: 'zoneCountAdd', add: 6 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'grand-halo',
    name: 'Grand halo',
    description: 'Tes bâtons lumineux frappent plus large.',
    family: 'class',
    classId: 'superfan',
    modifiers: [{ stat: 'attackRadiusMul', mul: 1.2 }],
    rarities: {
      rare: {
        description: 'Tes bâtons lumineux frappent nettement plus large.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 1.4 }],
      },
      legendary: {
        description: 'Tes bâtons lumineux frappent moitié plus large.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 1.5 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'grand-rappel',
    name: 'Grand rappel',
    description: 'Ta spéciale éclate plus large et plus fort.',
    family: 'class',
    classId: 'superfan',
    modifiers: [
      { stat: 'skillRadiusMul', mul: 1.25 },
      { stat: 'skillPowerMul', mul: 1.2 },
    ],
    rarities: {
      rare: {
        description: 'Ta spéciale éclate nettement plus large et plus fort.',
        modifiers: [
          { stat: 'skillRadiusMul', mul: 1.5 },
          { stat: 'skillPowerMul', mul: 1.4 },
        ],
      },
      legendary: {
        description: 'Ta spéciale éclate presque deux fois plus large, et fort.',
        modifiers: [
          { stat: 'skillRadiusMul', mul: 1.8 },
          { stat: 'skillPowerMul', mul: 1.6 },
        ],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'encore',
    name: 'Encore !',
    description: 'Ta spéciale revient plus vite.',
    family: 'class',
    classId: 'superfan',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ta spéciale revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ta spéciale revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
